import { motion } from "framer-motion";
import { Lightbulb, BrainCircuit, PenTool, Rocket, TrendingUp } from "lucide-react";
import { Reveal } from "./Reveal";

const AI_OPS = ["ANALYZE", "GENERATE", "OPTIMIZE"];

const POINTS = [
  { title: "FASTER IDEATION", desc: "Turn concepts into creative directions quickly." },
  { title: "SMARTER CONTENT", desc: "Create more relevant content across channels." },
  { title: "RAPID PRODUCTION", desc: "Accelerate image, video, design and campaign production." },
  { title: "SCALABLE MARKETING", desc: "Build systems that allow brands to grow without unnecessary complexity." },
];

const FlowNode = ({ icon: Icon, label, index }) => (
  <motion.div
    data-testid={`pipeline-node-${index}`}
    initial={{ opacity: 0, scale: 0.8 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className="flex flex-col items-center gap-3"
  >
    <motion.div
      animate={{
        boxShadow: [
          "0 0 18px -4px rgba(139,44,255,0.5)",
          "0 0 34px -4px rgba(196,60,255,0.85)",
          "0 0 18px -4px rgba(139,44,255,0.5)",
        ],
      }}
      transition={{ duration: 3.2, repeat: Infinity, delay: index * 0.5, ease: "easeInOut" }}
      className="w-16 h-16 rounded-full bg-[#0a0a10] border border-[#8B2CFF]/40 flex items-center justify-center"
    >
      <Icon className="w-6 h-6 text-[#C43CFF]" strokeWidth={1.5} />
    </motion.div>
    <span className="font-display text-xs font-bold uppercase tracking-[0.24em] text-white/85">
      {label}
    </span>
  </motion.div>
);

const AICore = () => (
  <motion.div
    data-testid="pipeline-node-1"
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="w-full max-w-sm"
  >
    <motion.div
      animate={{
        boxShadow: [
          "0 0 30px -10px rgba(139,44,255,0.5)",
          "0 0 64px -10px rgba(196,60,255,0.9)",
          "0 0 30px -10px rgba(139,44,255,0.5)",
        ],
      }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="glass-card rounded-lg border-[#8B2CFF]/40 p-8 text-center"
    >
      <div className="w-14 h-14 mx-auto rounded-full bg-[#6D00FF]/15 border border-[#8B2CFF]/50 flex items-center justify-center mb-4">
        <BrainCircuit className="w-6 h-6 text-[#E8B6FF]" strokeWidth={1.5} />
      </div>
      <span className="font-display text-sm font-extrabold uppercase tracking-[0.28em] text-white">
        AI Engine
      </span>
      <div className="flex justify-center gap-2.5 mt-5">
        {AI_OPS.map((op, i) => (
          <motion.span
            key={op}
            animate={{ opacity: [0.45, 1, 0.45] }}
            transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
            className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E8B6FF]/90 border border-[#8B2CFF]/30 rounded px-3 py-1.5"
          >
            {op}
          </motion.span>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

export const AIPipeline = () => (
  <section
    data-testid="ai-advantage-section"
    className="relative py-28 lg:py-40 bg-black overflow-hidden"
  >
    <div className="absolute inset-0 grid-bg opacity-60" aria-hidden="true" />
    <div
      aria-hidden="true"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#6D00FF]/10 blur-[180px] rounded-full pointer-events-none"
    />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
            The AI Advantage
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
            Built different. <span className="text-gradient-purple">Powered by AI.</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#A6A6B0] leading-relaxed">
            AI doesn't replace creativity. It gives great ideas the ability to move faster.
          </p>
        </Reveal>
      </div>

      <div className="relative max-w-md mx-auto mb-16 lg:mb-20" data-testid="ai-system">
        <div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-[#6D00FF] via-[#C43CFF] to-[#6D00FF] opacity-50"
        />
        <motion.div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 top-8 bottom-8 w-px"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(196,60,255,0.9) 50%, transparent 100%)",
            backgroundSize: "100% 300%",
          }}
          animate={{ backgroundPosition: ["0% -150%", "0% 150%"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
        <motion.span
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#E8B6FF] shadow-[0_0_18px_rgba(196,60,255,1)]"
          animate={{ top: ["3%", "97%"], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        />
        <motion.span
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#C43CFF] shadow-[0_0_12px_rgba(196,60,255,0.9)]"
          animate={{ top: ["3%", "97%"], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear", delay: 2 }}
        />
        <div className="relative flex flex-col items-center gap-10">
          <FlowNode icon={Lightbulb} label="IDEA" index={0} />
          <AICore />
          <FlowNode icon={PenTool} label="CREATIVE" index={2} />
          <FlowNode icon={Rocket} label="CAMPAIGN" index={3} />
          <FlowNode icon={TrendingUp} label="GROWTH" index={4} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1} className="h-full">
            <div
              data-testid={`ai-point-${i}`}
              className="h-full glass-card rounded-lg p-7 transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_36px_-12px_rgba(109,0,255,0.5)]"
            >
              <span className="block w-8 h-px bg-gradient-to-r from-[#6D00FF] to-[#C43CFF] mb-5" aria-hidden="true" />
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] mb-3">{p.title}</h3>
              <p className="text-sm text-[#A6A6B0] leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
